import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import {
  ArrayField,
  Datagrid,
  Error,
  FunctionField,
  Loading,
  TextField,
  useQuery,
} from 'react-admin';
import { Box, makeStyles } from '@material-ui/core';

import ExpandableQuizTable from 'resources/common/ExpandableQuizTable';
import { OfflineList } from 'resources/common';
import {
  getQuizModuleName,
  getQuizSubmoduleName,
  getQuizUnitName,
} from 'resources/common/quizProgress/quizPathUtils';

const useStyles = makeStyles({
  summaryCell: {
    textAlign: 'right',
    whiteSpace: 'nowrap',
    color: '#757575',
    fontSize: 13,
  },
});

const formatModuleSummary = (moduleRecord) => {
  const quizCount = R.length(R.propOr([], 'quizzes', moduleRecord));
  const submissionCount = R.pipe(
    R.propOr([], 'quizzes'),
    R.chain(R.propOr([], 'submissions')),
    R.length
  )(moduleRecord);
  const quizLabel = quizCount === 1 ? 'quiz' : 'quizzes';
  const entregaLabel = submissionCount === 1 ? 'entrega' : 'entregas';
  return `${quizCount} ${quizLabel} · ${submissionCount} ${entregaLabel}`;
};

const QuizList = ({ record, course, section = null }) => {
  const classes = useStyles();
  const [quizzes, setQuizzes] = useState([]);

  const {
    data: apiQuizzes,
    loading: quizzesLoading,
    error: quizzesError,
  } = useQuery({
    type: 'getManyReference',
    resource: 'quizzes',
    payload: {
      target: 'cohort',
      id: record.id,
      pagination: {
        perPage: 100,
      },
      filter: R.reject(R.isNil, { course, section, submissions: true }),
      sort: { field: 'first_name', order: 'ASC' },
    },
  });

  useEffect(() => {
    if (apiQuizzes) {
      const tableData = R.pipe(
        R.map((quiz) => {
          const averageScore = R.pipe(
            R.propOr([], 'submissions'),
            R.pluck('globalScore'),
            R.reject(R.isNil),
            (scores) =>
              R.isEmpty(scores)
                ? null
                : Math.round((R.mean(scores) + Number.EPSILON) * 100) / 100
          )(quiz);
          return R.assoc('average_score', averageScore, quiz);
        })
      )(apiQuizzes);
      setQuizzes(tableData);
    }
  }, [apiQuizzes, course]);

  if (quizzesLoading)
    return (
      <Box p="16px">
        <Loading />
      </Box>
    );
  if (quizzesError)
    return (
      <Box p="16px">
        <Error error={quizzesError} errorInfo={quizzesError} />
      </Box>
    );

  if (R.isEmpty(quizzes)) {
    return null;
  }

  const getModuleKey = getQuizModuleName;
  const getSubmoduleKey = getQuizSubmoduleName;
  const getUnitKey = getQuizUnitName;

  const modules = R.pipe(
    R.groupBy(getModuleKey),
    R.toPairs,
    R.map(([moduleName, moduleQuizzes]) => ({
      id: moduleName,
      display_name: moduleName,
      quizzes: moduleQuizzes,
      submodules: R.pipe(
        R.groupBy(getSubmoduleKey),
        R.toPairs,
        R.map(([submoduleName, submoduleQuizzes]) => ({
          id: `${moduleName}::${submoduleName}`,
          display_name: submoduleName,
          quizzes: submoduleQuizzes,
          units: R.pipe(
            R.groupBy(getUnitKey),
            R.toPairs,
            R.map(([unitName, unitQuizzes]) => ({
              id: `${moduleName}::${submoduleName}::${unitName}`,
              display_name: unitName,
              quizzes: unitQuizzes,
            }))
          )(submoduleQuizzes),
        })),
        R.reverse
      )(moduleQuizzes),
    })),
    R.reverse
  )(quizzes);

  const ModuleQuizzesTable = ({ record }) => {
    const unitQuizzes = R.pipe(
      R.propOr([], 'submodules'),
      R.chain(R.propOr([], 'units')),
      R.chain(R.propOr([], 'quizzes')),
      R.sortBy(getQuizUnitName)
    )(record);
    if (R.isEmpty(unitQuizzes)) return null;
    return (
      <OfflineList
        list={unitQuizzes}
        uniqueKey="id"
        defaultSort={{ field: 'path.2.display_name', order: 'ASC' }}
        sorters={{
          average_score: R.prop('average_score'),
        }}
        nullsLastFields={['average_score']}
      >
        <ExpandableQuizTable course={course} />
      </OfflineList>
    );
  };

  const ModuleDetail = ({ record }) => <ModuleQuizzesTable record={record} />;

  return (
    <ArrayField source="modules" record={{ modules }}>
      <Datagrid rowClick="expand" expand={<ModuleDetail />}>
        <TextField label="Module" source="display_name" sortable={false} />
        <FunctionField
          label=""
          sortable={false}
          textAlign="right"
          cellClassName={classes.summaryCell}
          headerClassName={classes.summaryCell}
          render={formatModuleSummary}
        />
      </Datagrid>
    </ArrayField>
  );
};

function ByQuizzesSubtab(props) {
  const { record, course } = props;
  return <QuizList record={record} course={course} />;
}

export default ByQuizzesSubtab;
